/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { config } from './config';

/** Shared axios client; token provider injected at runtime (no auth dependency). */
export const apiClient: AxiosInstance = axios.create({
  baseURL: config.api.baseUrl,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
});

type TokenProvider = () => Promise<string | null | undefined> | string | null | undefined;

let getToken: TokenProvider = () => null;

/** Register the async access-token provider (MSAL acquires tokens silently). */
export function setAuthTokenProvider(provider: TokenProvider): void {
  getToken = provider;
}

type AuthErrorHandler = (status: number, error: unknown) => void;

let onAuthError: AuthErrorHandler | null = null;

/** Register handler for 401/403 responses so the app can route to the access page. */
export function setAuthErrorHandler(handler: AuthErrorHandler | null): void {
  onAuthError = handler;
}

apiClient.interceptors.request.use(async (request: InternalAxiosRequestConfig) => {
  const token = await getToken();
  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`);
  }
  return request;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    // Route 401/403 to the access page in the matching mode.
    if ((status === 401 || status === 403) && onAuthError) {
      onAuthError(status, error);
    }
    if (import.meta.env.DEV) {
      console.error('[api] request failed', error?.config?.url, status);
    }
    return Promise.reject(error);
  },
);
